<template>
    <m-pageframe title="List Member Imports">
        <v-container fluid style="padding: 15px">
                    <imports-search-card
                :card_title="'List Member Import History'"
                endpoint_base="/api/v1/imports/contact-list"
                :buildParams="build_params_for_search"
                :refresh_key="refresh_key"
                loading_text="Loading contact list imports..."
                no_data_base_text="No contact list imports available"
            >
                <template #actions>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on">
                                <v-btn
                                    color="primary"
                                    :disabled="!hasWriteAccess()"
                                    @click="onClickImportContacts"
                                >
                                    Import List Members...
                                </v-btn>
                            </span>
                        </template>
                        <span v-if="!hasWriteAccess()">You do not have permission to create imports</span>
                        <span v-else>Import</span>
                    </v-tooltip>
                </template>

                <template #default="{ results, loading, footer_props, computed_no_data_text }">
                    <!-- Filters Row -->
                     <!--Disabled since I didn't have time to implement it correctly/it wasn't really requested.
                    <v-row class="mb-4">
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="status_filter"
                                :items="status_options"
                                label="Import Status"
                                clearable
                                @change="trigger_refresh"
                            />
                        </v-col>
                        <v-col cols="12" md="4">
                            <v-select
                                v-model="target_list_filter"
                                :items="list_options"
                                label="Target List"
                                clearable
                                @change="trigger_refresh"
                            />
                        </v-col>
                    </v-row>
-->
                    <imports-data-table
                        v-if="results != null"
                        :headers="filtered_table_headers"
                        :items="results"
                        :items-per-page="50"
                        :loading="loading"
                        loading_text="Loading contact list imports..."
                        :no_data_text="computed_no_data_text"
                        :footer_props="footer_props"
                        import_type="contact-list"
                        upload_route_name="contact-list-import-upload"
                    />
                </template>
            </imports-search-card>
        </v-container>

        <!-- List Selection Modal -->
        <v-dialog v-model="list_selection_dialog" max-width="600px">
            <v-card>
                <v-card-title>
                    Select Contact List
                    <v-spacer></v-spacer>
                    <v-btn icon @click="list_selection_dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    <p class="mb-4">Choose which contact list you want to import contacts into:</p>

                    <v-select
                        v-model="selected_list_id"
                        :items="list_options"
                        label="Contact List"
                        :loading="loading_contact_lists"
                        :disabled="loading_contact_lists"
                        item-text="text"
                        item-value="value"
                        outlined
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="list_selection_dialog = false">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="!selected_list_id"
                        @click="onConfirmListSelection"
                    >
                        Continue to Import
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </m-pageframe>
</template>

<script>
import ImportsSearchCard from '@/components/common/imports-search-card.vue';
import ImportsDataTable from '@/components/imports/imports-data-table.vue';

export default {
    components: { ImportsSearchCard, ImportsDataTable },
    name: 'SearchContactListImports',


    data() {
        return {
            // Search and filtering
            full_text: '',
            status_filter: '',
            target_list_filter: '',
            refresh_key: 0,

            // Contact lists for filtering
            contact_lists: [],
            loading_contact_lists: false,
            contact_lists_error: null,

            // List selection modal
            list_selection_dialog: false,
            selected_list_id: null,

            // Table configuration
            table_headers: [
                { text: 'Date', value: 'date', sortable: false },
                { text: 'Time', value: 'time', sortable: false },
                { text: 'Imported By', value: 'imported_by', sortable: false },
                { text: 'Original File', value: 'file_name', sortable: false },
                { text: 'Mapped File', value: 'enriched_data', sortable: false },
                { text: 'Status', value: 'state', sortable: false },
                { text: 'Target List', value: 'target_list_names', sortable: false },
                { text: 'List Members Added', value: 'list_members_added', sortable: false, align: 'center' },
                { text: 'Actions', value: 'actions', sortable: false }
            ],

            // Filter options will be computed from enum data
        };
    },

    computed: {
        computed_no_data_text() {
            if (this.full_text) {
                return `No contact list imports found matching "${this.full_text}"`;
            }
            return 'No contact list imports available';
        },

        footer_props() {
            return {
                itemsPerPageText: 'Imports per page:',
                itemsPerPageOptions: [25, 50, 100],
                disableItemsPerPage: false,
                disablePagination: false
            };
        },

        filtered_table_headers() {
            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                return this.table_headers;
            } else {
                return this.table_headers.filter((item) => item.showForStaffOnly !== true);
            }
        },

        status_options() {
            const options = [{ value: '', text: 'All Statuses' }];
            const enum_choices = this.$rc.enumChoices('import_state');

            // Skip the first "Any" option and add the rest
            for (let i = 1; i < enum_choices.length; i++) {
                options.push({
                    value: enum_choices[i].value,
                    text: enum_choices[i].text
                });
            }

            return options;
        },

        list_options() {
            const options = [{ value: '', text: 'All Lists' }];

            // Show loading state
            if (this.loading_contact_lists) {
                options.push({ value: '', text: 'Loading lists...', disabled: true });
                return options;
            }

            // Show error state
            if (this.contact_lists_error) {
                options.push({ value: '', text: 'Error loading lists', disabled: true });
                return options;
            }

            // Add loaded contact lists
            if (this.contact_lists && this.contact_lists.length > 0) {
                this.contact_lists.forEach(list => {
                    options.push({
                        value: list.value,
                        text: list.text
                    });
                });
            } else {
                options.push({ value: '', text: 'No lists available', disabled: true });
            }

            return options;
        }
    },

    mounted() {
        this.loadContactLists();
    },

    methods: {
        build_params_for_search(full_text, max_results) {
            // Compose full_text with filters by appending status and list label
            let composed = full_text || '';

            if (this.status_filter) {
                composed += (composed ? ' ' : '') + this.status_filter;
            }
            if (this.target_list_filter) {
                const selected_list = this.contact_lists.find(list => list.value === this.target_list_filter);
                if (selected_list) {
                    composed += (composed ? ' ' : '') + selected_list.text;
                }
            }

            return {
                start: 0,
                count: max_results,
                full_text: composed.trim()
            };
        },

        trigger_refresh() {
            // Bump refresh key to trigger shared component reload
            this.refresh_key = (this.refresh_key || 0) + 1;
        },

        onClickImportContacts() {
            this.list_selection_dialog = true;
            this.selected_list_id = null;
        },

        onConfirmListSelection() {
            if (!this.selected_list_id) {
                return;
            }

            this.list_selection_dialog = false;

            this.$router.push({
                name: 'contact-list-import-upload',
                params: {
                    organization_id: this.$route.params.organization_id
                },
                query: {
                    list_id: this.selected_list_id
                }
            });
        },



        hasWriteAccess() {
            // Check if current user has write access to contact list imports
            const organization_id = this.$route.params.organization_id;

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                // RedChirp staff can write in any organization (UI will warn on real accounts)
                return true;
            } else if (this.$rc.$state.isOrganizationAdmin(organization_id)) {
                // Organization admins can write to their organization
                return true;
            }

            // Regular users cannot write
            return false;
        },



        async loadContactLists() {
            if (this.loading_contact_lists) {
                return; // Prevent multiple simultaneous requests
            }

            this.loading_contact_lists = true;
            this.contact_lists_error = null;

            try {
                this.$rc.rest({
                    spinner: false,
                    endpoint: 'LIST: /api/v1/lists/choose-org-list',
                    params: {
                        organization_id: this.$route.params.organization_id,
                        include_auto_lists: false
                    },
                    on_success: (data) => {
                        this.contact_lists = data.results || [];
                        this.loading_contact_lists = false;
                        this.contact_lists_error = null;
                    },
                    on_error: (error) => {
                        this.loading_contact_lists = false;
                        this.contact_lists_error = 'Failed to load contact lists';
                        this.contact_lists = [];

                        if (error && error.message) {
                            this.$rc.snackbar(`Failed to load contact lists: ${error.message}`, 'error');
                        } else {
                            this.$rc.snackbar('Failed to load contact lists for filtering. Please refresh the page to try again.', 'error');
                        }
                    }
                });
            } catch (error) {
                this.loading_contact_lists = false;
                this.contact_lists_error = 'Unexpected error loading contact lists';
                this.contact_lists = [];
                this.$rc.snackbar('An unexpected error occurred while loading contact lists', 'error');
            }
        }
    }
};
</script>

<style scoped>
/* Contact list import search specific styles */
.target-lists-display {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 300px;
}

/* List-specific styling */
.target-list-chip {
    background-color: var(--v-purple-lighten4);
    color: var(--v-purple-darken2);
    font-weight: 500;
}

.import-type-chip {
    font-size: 11px;
    font-weight: bold;
    text-transform: uppercase;
}

.list-import-stats {
    display: flex;
    gap: 16px;
    align-items: center;
}

.stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 80px;
}

.stat-value {
    font-size: 18px;
    font-weight: bold;
    color: var(--v-primary-base);
}

.stat-label {
    font-size: 12px;
    color: var(--v-grey-darken1);
    text-transform: uppercase;
}
</style>
